import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { Message } from '../../api/types';
import { todayISO } from '../../utils/time';
import { useAuth } from '../../state/auth';
import { useToast } from '../../components/Toast';
import { AsyncButton } from '../../components/AsyncButton';

function fmtDT(s: string) {
  return new Intl.DateTimeFormat('it-IT', { dateStyle:'short', timeStyle:'short' }).format(new Date(s));
}

export function MessagesPage() {
  const { user } = useAuth();
  const { push } = useToast();
  const [items, setItems] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [workDate, setWorkDate] = useState(() => todayISO());
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get<{items: Message[]}>('/api/messages');
      setItems(r.items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function send() {
    if (!body.trim()) return;
    if (sending) return;
    setSending(true);
    try {
      await api.post('/api/messages', { work_date: workDate, body: body.trim() });
      setBody('');
      push({ title: 'Messaggio inviato' });
      await load();
    } catch (e: any) {
      push({ title: 'Invio fallito', body: e?.data?.error || e?.message });
    } finally {
      setSending(false);
    }
  }

  const grouped = useMemo(() => items.slice().sort((a,b) => +new Date(a.sent_at) - +new Date(b.sent_at)), [items]);

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="h2" style={{ marginTop:0 }}>Messaggi</div>
        <div className="small">Invia un messaggio al titolare associandolo a una giornata.</div>

        <div className="grid" style={{ gridTemplateColumns:'1fr', gap: 12, marginTop: 12 }}>
          <div>
            <div className="small">Giorno</div>
            <input className="input" type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </div>
          <div>
            <div className="small">Messaggio (max 500)</div>
            <textarea className="input" style={{ minHeight: 110 }} value={body} onChange={(e) => setBody(e.target.value.slice(0, 500))} />
          </div>
          <AsyncButton className="btn btnPrimary" onClickAsync={send} disabled={!body.trim() || sending} busyText="Invio…">Invia</AsyncButton>
        </div>
      </div>

      <div className="card">
        <div className="spread">
          <div className="h3" style={{ margin: 0 }}>Thread</div>
          <AsyncButton className="btn btnSecondary" onClickAsync={load} disabled={loading} busyText="Aggiorno…">Aggiorna</AsyncButton>
        </div>

        <div style={{ marginTop: 12, display:'grid', gap: 10 }}>
          {grouped.map(m => {
            const incoming = !!user && m.to_user_id === user._id;
            return (
              <div key={m._id} className="card" style={{ background: incoming ? 'var(--surface-muted)' : 'var(--surface-accent)', boxShadow:'none' }}>
                <div className="small">{fmtDT(m.sent_at)}</div>
                <div style={{ marginTop: 6, fontWeight: 700 }}>{m.body}</div>
              </div>
            );
          })}
          {grouped.length === 0 ? <div className="small">Nessun messaggio.</div> : null}
        </div>
      </div>
    </div>
  );
}
